/**
 * useGeneratedDocExport — IMPL-002 PR-2.
 *
 * Thin orchestrator hook for the Export Review modal, mirroring
 * useDeadlineCalendarExport (the Layer 4 reference implementation). The
 * submit pipeline (acknowledgment → gate → download) lives inside
 * `ExportReviewModal` itself; this hook only:
 *  - holds open/closed state and the document + format + artifact context,
 *  - returns a bound `<Modal />` component the caller renders in its tree,
 *  - exposes `open(params)` / `close()` for export-menu triggers.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  ExportReviewModal,
  type ExportReviewModalProps,
} from '../components/casemaker/ExportReviewModal';
import type {
  GeneratedDocument,
  ExportFormat,
  ExportArtifact,
} from '../types/generatedDoc';

export interface GeneratedDocExportParams {
  document: GeneratedDocument;
  format: ExportFormat;
  artifact: ExportArtifact;
}

export interface UseGeneratedDocExportResult {
  /** Open the review modal for a document + format + artifact selection. */
  open: (params: GeneratedDocExportParams) => void;
  /** Close the modal without exporting. */
  close: () => void;
  /** Read-only state — useful for tests + parent UX. */
  state: {
    isOpen: boolean;
    params: GeneratedDocExportParams | null;
  };
  /**
   * Bound modal component. Caller renders `<Modal />` once somewhere in its
   * tree; the hook supplies the props from internal state.
   */
  Modal: React.FC;
}

export function useGeneratedDocExport(options?: {
  onExported?: ExportReviewModalProps['onExported'];
  /** PR-3: fired on a DOCUMENT_CHANGED denial so the parent can refresh. */
  onDocumentChanged?: ExportReviewModalProps['onDocumentChanged'];
}): UseGeneratedDocExportResult {
  const [isOpen, setIsOpen] = useState(false);
  const [params, setParams] = useState<GeneratedDocExportParams | null>(null);

  const open = useCallback((p: GeneratedDocExportParams) => {
    setParams(p);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onExported = options?.onExported;
  const onDocumentChanged = options?.onDocumentChanged;

  const Modal = useMemo<React.FC>(() => {
    const Bound: React.FC = () => {
      if (!params) return null;
      return (
        <ExportReviewModal
          isOpen={isOpen}
          onClose={close}
          document={params.document}
          format={params.format}
          artifact={params.artifact}
          onExported={(result) => {
            onExported?.(result);
          }}
          onDocumentChanged={onDocumentChanged}
        />
      );
    };
    Bound.displayName = 'BoundExportReviewModal';
    return Bound;
  }, [isOpen, params, close, onExported, onDocumentChanged]);

  return {
    open,
    close,
    state: { isOpen, params },
    Modal,
  };
}

export default useGeneratedDocExport;
